<template>
    <section
      :class="[
        'wrapper',
        'content',
        'content--centered',
        $arMediaQuery.pageContent.maxWidth('sm') && 'sm-max',
        $arMediaQuery.pageContent.maxWidth('xs') && 'xs-max',
      ]">
      <am2-step-link
        class="step-back-link"
        text="Back to integrations"
        @click="handleBackLinkClick" />
      <am2-tixr-modal-new
        :is-show="showTixrModal"
        :loading="isCreatingTixrIntegration"
        @confirm="handleTixrConfirm"
        @cancel="showTixrModal = false"
      />

      <div
        :class="[
          'title-section',
          $arMediaQuery.pageContent.maxWidth('md') && 'md-max',
        ]">
        <div class="title-section-left">
          <am2-icon-title-section
            title="Tixr"
            description="Automatically sync your members, events, orders from Tixr"
            :icon-props="{
              name: 'tixr',
              color: $arStyle.color.tixr,
              height: '46px',
              width: '40px',
            }"
          />
        </div>
        <div class="title-section-right" v-if="!isFetchingTixrIntegration && integrationsSummary">
          <am2-integration-account-select
            v-if="!isFetchingTixrIntegration && tixrIntegration && currentIntegration"
            :default-selected-integration-oid="currentIntegration.oid"
            :available-integrations="integrationsSummary"
            @select="handleIntegrationChange"
            @new-account="handleAddNewIntegration"
            :style="{ marginRight: '10px', maxWidth: 'calc(100vw - 80px)', }"
          />
          <am2-expand-button-dropdown
            align="left"
            :button-props="{ iconName: 'settings' }"
            :items="[
              {
                name: 'Reconnect Integration',
                value: 'reconnectIntegration',
              },
              {
                name: 'Remove Account',
                value: 'removeIntegration',
              },
            ]"
            @select="handleSettingOptionSelect" />
        </div>
      </div>
      <am2-tabs
        :items="[
          { title: 'Notifications' },
          { title: 'Status' }
        ]"
        :selected-tab-index="selectedTabIndex"
        class="tab-options"
        @select="(selected, idx) => selectedTabIndex = idx"
      />
      <div
        v-if="selectedTabIndex === 0"
      >
        <NotificationsContainer
          :integration="currentIntegration"
          :loading="isFetchingTixrIntegration"
        />
      </div>
      <div
        v-if="selectedTabIndex === 1"
        class="integration-status-container"
      >
        <section v-if="isAddingTixrIntegration || isFetchingTixrIntegration" class="integration-loading-container">
          <am2-loading-bubble />
        </section>
        <section v-else>
          <section v-if="integrationFailed" class="integration-failed-message">
            <ar-snackbar
              type="warning"
              message="Your Tixr connection is no longer valid. This means we can't sync your member data. <a>Please reconnect your account</a>"
              @anchorClick="handleReconnectIntegration"
              :style="{ width: '100%' }"
            />
          </section>
    
          <SyncBlockContainer
            v-else
            class="sync-block-container"
            :is-fetching-sync-task="isFetchingSyncTask"
            :sync-status="syncStatus"
            :sync-time="syncTime"
            :sync-copies="{
              'in-progress': 'Syncing data from Tixr',
              'pending': 'The data sync is still pending',
              'completed': 'Your Tixr account is currently connected',
              'cancelled': 'Your Tixr account connection is cancelled',
              'failed': this.syncErrorMessage,
              'default': 'Syncing tasks not found',
            }"
            @startSync="handleStartSync"
            @stopSync="handleStopSync"
          />
        </section>
      </div>
    </section>
  </template>
  
  <script>
  import { mapState, mapActions, mapMutations } from 'vuex';
  import SyncBlockContainer from '../components/sync-block-container';
  import NotificationsContainer from '../components/notifications-container';
  
  export default {
    name: 'TixrIntegration',
    layout: 'default',
    components: {
      SyncBlockContainer,
      NotificationsContainer
    },
  
    data: () => ({
      currentlySelectedIntegrationIndex: 0,
      syncStatusPoller: null,
      showTixrModal: false,
      selectedTabIndex: 1
    }),
  
    computed: {
      ...mapState({
        tixrIntegration: state => state.tixrIntegration.integration,
        isFetchingTixrIntegration: state => state.tixrIntegration.isFetchingIntegration,
        isAddingTixrIntegration: state => state.tixrIntegration.isAddingIntegration,
        isCreatingTixrIntegration: state => state.tixrIntegration.isCreatingIntegration,
        isDeletingTixrIntegration: state => state.tixrIntegration.isDeletingIntegration,
        isFetchingSyncTask: state => state.tixrIntegration.isFetchingSyncTask,
        syncTask: state => state.tixrIntegration.syncTask,
        syncStatus: state => state.tixrIntegration.syncStatus,
      }),
  
      tixrIntegrationEnabled() {
        return true;
      },
  
      syncErrorMessage() {
        return this.syncTask?.statusDetails?.lastError || this.syncTask?.statusDetails?.reason;
      },
  
      syncTime() {
        return this.syncTask?.sysMtime || null;
      },
  
      integrationsSummary() {
        if (!this.tixrIntegration) return null;
        const summary = this.tixrIntegration
          .map(i => ({
            oid: i.oid,
            name: i.accountId,
            email: i.accountId
          }));
        return summary
      },
  
      currentIntegration() {
        if (this.currentlySelectedIntegrationIndex === null) return null;
        if (!this.tixrIntegration || this.tixrIntegration.length < 1 || !this.tixrIntegration[this.currentlySelectedIntegrationIndex]) return null;
        return this.tixrIntegration[this.currentlySelectedIntegrationIndex];
      },
  
      integrationFailed() {
        if (!this.currentIntegration) return false;
        return this.currentIntegration.status === 'failed';
      },
    },
  
    async mounted() {
      if (!this.tixrIntegrationEnabled) {
        this.handleBackLinkClick();
      }
      await this.fetchIntegration();
      this.startFetchTasksPolling();
    },
  
    beforeDestroy() {
      this['tixrIntegration/RESET_INTEGRATION']();
  
      if (this.syncStatusPoller) {
        clearInterval(this.syncStatusPoller);
      }
    },
  
    methods: {
      ...mapActions([
        'SHOW_CONFIRM',
        'tixrIntegration/FETCH_INTEGRATION',
        'tixrIntegration/DELETE_INTEGRATION',
        'tixrIntegration/SYNC_START',
        'tixrIntegration/SYNC_STOP',
        'tixrIntegration/FETCH_SYNC_TASK',
        'tixrIntegration/CREATE_TIXR_INTEGRATION',
        'promoterTasks/START_POLLING_PENDING_TASKS',
      ]),
      ...mapMutations([
        'tixrIntegration/RESET_INTEGRATION',
      ]),
      startFetchTasksPolling() {
        if (this.currentIntegration?.oid && !this.isFetchingSyncTask) {
          this.fetchTasks();
          this.syncStatusPoller = setInterval(this.fetchTasks, 3000);
        }
      },
      async fetchIntegration() {
        const success = await this['tixrIntegration/FETCH_INTEGRATION']()
        if ((!success || !this.tixrIntegration || this.tixrIntegration.length === 0) && this.$route.path != '/settings/integrations') {
          this.$router.push({
            path: '/settings/integrations',
          });
        }
      },
  
      async fetchTasks() {
        await this['tixrIntegration/FETCH_SYNC_TASK'](this.currentIntegration.oid);
  
        if (this.currentIntegration && this.hasExistingTask && this.syncStatus !== "in-progress") {
          window.clearInterval(this.syncStatusPoller);
          this.syncStatusPoller = null;
          this['promoterTasks/START_POLLING_PENDING_TASKS']({});
        }
      },
  
      async handleTixrConfirm({ clientPartnerKey, privateKey, accountName }) {
        const succeed = await this['tixrIntegration/CREATE_TIXR_INTEGRATION']({
          clientPartnerKey,
          privateKey,
          accountName
        });
        if (succeed) {
          this.fetchIntegration();
  
          this.showTixrModal = false;
        }
      },
  
  
      hasExistingTask() {
        return this.syncTask !== null;
      },
  
      handleBackLinkClick() {
        this.$router.push({ path: '/settings/integrations' });
      },
  
      handleIntegrationChange(integration, index) {
        this.currentlySelectedIntegrationIndex = index;
  
  
        this.fetchTasks();
      },
  
      async handleAddNewIntegration() {
        this.showTixrModal = true;
      },
  
      async handleStartSync() {
        setTimeout(() => {
          this['promoterTasks/START_POLLING_PENDING_TASKS']({});
        }, 750);
        await this['tixrIntegration/SYNC_START']({ oid: this.currentIntegration.oid });
        if (this.syncStatus !== 'completed') {
          this.syncStatusPoller = setInterval(this.fetchTasks, 3000);
        }
      },
  
      handleStopSync() {
        this['tixrIntegration/SYNC_STOP']({oid: this.currentIntegration.oid});
        setTimeout(() => {
          this['promoterTasks/START_POLLING_PENDING_TASKS']({});
        }, 750);
      },
  
      async handleReconnectIntegration() {
        const agreed = await this.SHOW_CONFIRM({
          messageHtml: `Make sure you are logging in with the Tixr account <strong>${this.currentIntegration.accountId}</strong> on Tixr before reconnecting it.`,
          confirmButtonText: 'Reconnect account',
        });
        if (!agreed) {
          return;
        }
  
        const succeed = await this['tixrIntegration/DELETE_INTEGRATION'](this.currentIntegration.oid);
        if (!succeed) {
          return;
        }
  
        this.handleAddNewIntegration();
      },
  
      async handleSettingOptionSelect(item) {
        if (item.value === 'removeIntegration') {
          const answer = await this.SHOW_CONFIRM({
            messageHtml: 'Removing the Tixr integration will stop syncing your orders and customer data from Tixr.',
            confirmButtonText: 'Remove integration',
            iconName: 'alert-question-mark',
            title: 'Are you sure?',
          });
          if (answer) {
            try {
              await this['tixrIntegration/DELETE_INTEGRATION'](this.currentIntegration.oid);
              if (this.tixrIntegration.length > 0) {
                this.currentlySelectedIntegrationIndex = 0;
                await this.fetchIntegration();
                this.startFetchTasksPolling();
              } else {
                this.$router.push({ path: '/settings/integrations' });
              }
            } catch (e) {
              this.$arNotification.push({ type: 'error', message: 'Failed to remove integration' });
            }
          }
        } else if (item.value === 'reconnectIntegration') {
          this.handleReconnectIntegration();
        } else if (item.value == 'openSettingsModal') {
          if (!this.currentIntegration) return;
        }
      },
    }
  };
  </script>
  
  <style lang="scss" scoped>
  .wrapper {

    .tab-options {
      margin: 60px 0 20px 0;
      border-bottom: 1px $skyBlueGrey500 solid;
    }
  
    &.sm-max {
      padding: 0 24px;
    }
    &.xs-max {
      padding: 0 12px;
    }
  
    .integration-loading-container {
      display: flex;
      justify-content: center;
      margin-top: 65px;
    }
  
  
    .step-back-link {
      margin-top: 48px;
    }
  
    .title-section {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 48px;
  
      .title-section-left {
        display: inline-flex;
        align-items: flex-start;
  
        .tag {
          position: relative;
          margin-left: 10px;
          top: 4px;
        }
      }
  
      .title-section-right {
        display: inline-flex;
        align-items: center;
  
        .select {
          width: 300px;
          margin-right: 10px;
        }
      }
  
      &.md-max {
        flex-direction: column;
        align-items: flex-start;
  
        .title-section-right {
          margin-top:16px;
        }
      }
    }
  
  
    .sync-block-container {
      margin-top:0px;
    }
  }
  </style>
  